import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { GoalNameInput } from './GoalNameInput';
import { GoalTypeSelector } from './GoalTypeSelector';
import { GoalAmountInput } from './GoalAmountInput';
import { GoalConfirmation } from './GoalConfirmation';
import './AddGoalFlow.css';

export type GoalType = 'with_amount' | 'piggybank';

export interface GoalData {
  name: string;
  icon?: string;
  type: GoalType;
  target_amount?: number;
}

interface AddGoalFlowProps {
  onClose: () => void;
  onSubmit: (data: GoalData) => Promise<void>;
  currency?: string;
  currencyCode?: string;
}

type Step = 'name' | 'type' | 'amount' | 'confirm';

export const AddGoalFlow: React.FC<AddGoalFlowProps> = ({ 
  onClose, 
  onSubmit, 
  currency = 'RUB', 
  currencyCode 
}) => {
  const finalCurrency = currencyCode || currency;
  const [step, setStep] = useState<Step>('name');
  const [data, setData] = useState<Partial<GoalData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingFromConfirm, setIsEditingFromConfirm] = useState(false);

  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Восстанавливаем скролл при закрытии
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Определяем порядок шагов
  const getSteps = (): Step[] => {
    const baseSteps: Step[] = ['name', 'type'];
    
    // Добавляем ввод суммы только если выбран тип "с суммой"
    if (data.type === 'with_amount') {
      baseSteps.push('amount');
    }
    
    baseSteps.push('confirm');
    return baseSteps;
  };

  const steps = getSteps();
  const currentStepIndex = steps.indexOf(step);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const updateData = (updates: Partial<GoalData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const goToNextStep = () => {
    // Если редактируем с экрана подтверждения, возвращаемся к подтверждению
    if (isEditingFromConfirm) {
      setIsEditingFromConfirm(false);
      setStep('confirm');
      return;
    }
    
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setStep(steps[nextIndex]);
    }
  };

  const goToPreviousStep = () => {
    // Если редактируем с экрана подтверждения и нажали "Назад", возвращаемся к подтверждению
    if (isEditingFromConfirm) {
      setIsEditingFromConfirm(false);
      setStep('confirm');
      return;
    }
    
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(steps[prevIndex]);
    } else {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!isDataComplete()) return;
    
    try {
      setIsSubmitting(true);
      await onSubmit(data as GoalData);
    } catch (error) {
      console.error('Error submitting goal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDataComplete = (): boolean => {
    if (!data.name || !data.type) return false;
    
    // Если тип "с суммой", проверяем наличие суммы
    if (data.type === 'with_amount' && (!data.target_amount || data.target_amount <= 0)) {
      return false;
    }
    
    return true;
  };

  const renderStep = () => {
    switch (step) {
      case 'name':
        return (
          <GoalNameInput
            value={data.name || ''}
            icon={data.icon}
            onChange={(name, icon) => updateData({ name, icon })}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );

      case 'type':
        return (
          <GoalTypeSelector
            value={data.type}
            onChange={(type) => {
              // Если меняем тип, сбрасываем сумму
              updateData({ 
                type, 
                target_amount: type === 'piggybank' ? undefined : data.target_amount 
              });
            }}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );

      case 'amount':
        return (
          <GoalAmountInput
            value={data.target_amount || 0}
            onChange={(target_amount) => updateData({ target_amount })}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            currency={finalCurrency}
          />
        );

      case 'confirm':
        return (
          <GoalConfirmation
            data={data as GoalData}
            onConfirm={handleSubmit}
            onEdit={(editStep) => {
              setIsEditingFromConfirm(true);
              setStep(editStep);
            }}
            onBack={goToPreviousStep}
            isSubmitting={isSubmitting}
            currency={finalCurrency}
          />
        );

      default:
        return null;
    }
  };

  return createPortal(
    <div className="add-goal-flow">
      <div className="add-goal-flow__overlay" onClick={onClose} />
      <div className="add-goal-flow__container">
        <div className="add-goal-flow__content">
          {renderStep()}
        </div>
      </div>
    </div>,
    document.body
  );
};

